import React, {useContext, useEffect, useState} from 'react';
import {Navigate} from 'react-router-dom';
import {UserContext} from '../context/userContext';
import AuthService from '../services/auth.service';
import axios from 'axios';
import {API_BASE_URL, MEDIA_TYPE_V2} from '../config/constants';
import {Box, CircularProgress} from '@mui/material';

interface ProtectedRouteProps {
    children: React.ReactNode;
}

/**
 * ProtectedRoute component that verifies authentication before rendering children.
 * This handles authentication at the routing level for both Okta and traditional login.
 *
 * Authentication flow:
 * 1. Check if user exists in localStorage (set by both auth methods)
 * 2. Verify the HttpOnly cookie is valid with a test API call
 * 3. Redirect to signin if not authenticated
 * 4. Render children if authenticated
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
    const { user } = useContext(UserContext);
    const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
    const [isChecking, setIsChecking] = useState(true);


    useEffect(() => {

        const verifyAuthentication = async (): Promise<void> => {
            try {
                // First check if user exists in context/localStorage
                // This is set by both Okta and traditional login flows
                const currentUser = AuthService.getCurrentUser();

                if (!currentUser) {
                    // No user in localStorage means not logged in
                    setIsAuthenticated(false);
                    setIsChecking(false);
                    return;
                }

                // User exists in localStorage, but we need to verify the HttpOnly cookie
                // This handles the case where the cookie might have expired or been deleted
                // Both auth methods set the same HttpOnly cookie named 'access_token'

                try {
                    // Use a lightweight endpoint to verify the cookie is valid
                    // This will fail with 401 if the cookie is missing or invalid
                    await axios.get(API_BASE_URL + 'systemInfo/system', {
                        withCredentials: true,  // This sends the HttpOnly cookie
                        headers: { 'Content-Type': MEDIA_TYPE_V2 }
                    });

                    // API call succeeded, authentication is valid
                    setIsAuthenticated(true);
                } catch (error: any) {
                    // API call failed - check if it's an auth error
                    if (error.response?.status === 401) {
                        // Cookie is invalid or missing

                        // Clear the stale user data from localStorage
                        localStorage.removeItem('userProfile');
                        setIsAuthenticated(false);
                    } else {
                        // Some other error (network, server error, etc.)

                        // In case of network/server errors, check if we're in Okta callback flow
                        // The callback URL will have 'code' and 'state' parameters
                        const urlParams = new URLSearchParams(window.location.search);
                        const isOktaCallback = urlParams.has('code') && urlParams.has('state');

                        if (isOktaCallback) {
                            // We're in the middle of Okta callback, allow it to proceed
                            // The loginCallback page will handle the authentication
                            setIsAuthenticated(true);
                        } else {
                            // Not in callback flow and can't verify auth
                            setIsAuthenticated(false);
                        }
                    }
                }
            } catch (error) {
                setIsAuthenticated(false);
            } finally {
                setIsChecking(false);
            }
        };

        verifyAuthentication();
    }, [user]); // Re-check when user context changes

    // Show loading state while checking authentication
    if (isChecking) {
        return (
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100vh',
                gap: 2
            }}>
                <CircularProgress />
                <Box sx={{ color: 'text.secondary', fontSize: '16px' }}>
                    Verifying authentication...
                </Box>
            </Box>
        );
    }

    // Redirect to signin if not authenticated
    if (isAuthenticated === false) {
        // Store the attempted URL for redirect after login
        // This works for both Okta and traditional login flows
        const currentPath = window.location.pathname;
        if (currentPath !== '/react/signin' && currentPath !== '/react/login/callback') {
            sessionStorage.setItem('redirectUrl', currentPath);
        }
        return <Navigate to="/react/signin" replace />;
    }

    // Render children if authenticated
    return <>{children}</>;
};

export default ProtectedRoute;